import { useEffect, useMemo } from 'react';

import { useGetSensorsDataByEventAndIds } from '@realTimeDataAccess';
import { FlexAligner, Tooltip, Typography } from '@scaleaq/scaleui';
import {
  InventoryHelper,
  ISensorReading,
  ISiteControlDeviceChanged,
  ISiteControlDeviceInfo,
  SiteControlDeviceChanged,
} from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';
import { uncapitalize } from 'remeda';
import { v4 as uuidv4 } from 'uuid';

import { InformationModal } from '@/components/informationModal/InformationModal.tsx';
import { SensorCard } from '@/components/SensorCard.tsx';
import { SensorWidgetInfoFields } from '@/components/SensorWidgetInfoFields.tsx';
import { Stat } from '@/components/stat/';
import { useInventory } from '@/dataAccess/rest';
import { useOpenModal } from '@/hooks';
import { useSensorLedStateStorage } from '@/stores';
import { ComponentType, GenericWidgetProps } from '@/types.ts';
import { getValueByProvider, Icon } from '@/utils';
import { SENSOR_WIDGET_COMPONENT_NAME } from '@/widgetComponentNames.ts';

type SensorWidgetType = { id: string } & GenericWidgetProps;

export const SensorWidget: ComponentType<SensorWidgetType> = ({
  id: inventoryId,
  ...rest
}: SensorWidgetType) => {
  const { t } = useTranslation(['common']);
  const { updateDevicesConnectionState } = useSensorLedStateStorage();

  const { results, isLoading } = useGetSensorsDataByEventAndIds<
    ISiteControlDeviceChanged<ISiteControlDeviceInfo & ISensorReading>
  >([inventoryId], new SiteControlDeviceChanged().className);
  const sensor = useMemo(() => results[inventoryId], [results, inventoryId]);
  const sensorInfo = useMemo(() => sensor?.deviceInfo, [sensor?.deviceInfo]);
  const infoModalFunctions = useOpenModal();

  const { data: sensorInventoryData } = useInventory(inventoryId);
  const numericSensorProvider = useMemo(
    () =>
      (sensorInfo?.privateProviders ?? sensorInventoryData?.privateProviders)?.filter(
        (provider: string) => !InventoryHelper.stateProviders?.includes(provider)
      ),
    [sensorInfo?.privateProviders, sensorInventoryData?.privateProviders]
  );

  useEffect(() => {
    updateDevicesConnectionState(inventoryId, sensor?.isConnected);
  }, [sensor?.isConnected, inventoryId, updateDevicesConnectionState]);

  const toolbarActionList = useMemo(
    () => [
      {
        label: t('info'),
        id: uuidv4(),
        onClick: () => infoModalFunctions.handleOpenModal(),
        Icon: Icon.Info,
      },
    ],
    [infoModalFunctions, t]
  );
  return (
    <>
      <SensorCard
        {...rest}
        title={(sensorInfo?.name ?? sensorInventoryData?.name) || t('unnamedSensor')}
        isError={!sensorInfo?.isConnected}
        isLoading={isLoading}
        toolbarActionList={toolbarActionList}
        status={sensorInfo?.status}
        connectionBadge={
          !sensor?.isConnected
            ? { label: t('disconnected'), type: 'alarm' }
            : { label: t('connected'), type: 'success' }
        }
        body={
          <Stat
            translations={{
              units: {},
              labels: InventoryHelper.inventoryProviderFriendlyNamePair,
            }}
            values={(numericSensorProvider || []).map(param => {
              const value = sensorInfo
                ? getValueByProvider(sensorInfo, param)?.toString() // sensorInfo[uncapitalize(param) as unknown as keyof OrbitEnvironmentSensorInfo]
                : '--';

              return {
                id: uncapitalize(param),
                name: param,
                value: !isNaN(Number(value)) ? Number(value).toFixed(2) : String(value),
              };
            })}
          />
        }
        footer={
          <FlexAligner alignment='center-left' gap='0.3125rem' fullWidth={false}>
            <Icon.Info size='large' />
            <Typography type='body_small'>{`${t('lastUpdated')}:`}</Typography>
            <Tooltip tooltip={sensorInfo?.date ? new Date(sensorInfo?.date).toLocaleString() : ''}>
              <Typography type='body_small'>
                {sensorInfo?.date ? new Date(sensorInfo?.date).toLocaleTimeString() : '-'}
              </Typography>
            </Tooltip>
          </FlexAligner>
        }
      />
      <InformationModal
        name={sensorInfo?.name || ''}
        key={sensorInfo?.id}
        customReactJsonObject={sensorInfo}
        {...infoModalFunctions}
      >
        {sensorInfo && <SensorWidgetInfoFields sensor={sensorInfo} />}
      </InformationModal>
    </>
  );
};

SensorWidget.componentName = SENSOR_WIDGET_COMPONENT_NAME;
